import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { SignUp, type TooltipWithButtonProps } from '@which/seatbelt'
import { dynamicDatalayerPush } from '@which/shared'

import type {
  AnnualRunningCost as AnnualRunningCostType,
  Features,
  ImageWithSources,
  ProductListingPageQuery,
  SavedItemsQuery,
  TestScore,
} from '../../../../../generated/frontend'
import { InfoBanner } from '../../../../../shared/components/InfoBanner'
import { ReviewsCompareTray } from '../../../../../shared/components/ReviewsCompareTray/ReviewsCompareTray'
import { SkipButton } from '../../../../../shared/components/SkipButton/SkipButton'
import { CompareTrayContext } from '../../../../../shared/contexts/CompareTrayContext'
import { modalImageProps } from '../../../shared/data'
import { getIsSelectedForComparison } from '../../../utils/comparison'
import { ArticleListingWithImage } from '../ArticleListingWithImage'
import { NO_AVAILABLE_PRODUCTS } from './constants'
import { ProductCard } from './ProductCard'
import styles from './Products.module.scss'

export const Products = React.forwardRef<HTMLUListElement, ReviewsProductsProps>(
  (
    {
      products,
      taxonomySlug,
      scoreStatement,
      articleListingWithImage,
      loginStatus,
      savedProducts,
      ...rest
    },
    ref
  ) => {
    // Saved products state to store changes between data-provider requests
    const [savedProductsState, setSavedProductsState] = useState(
      savedProducts === null ? [] : savedProducts.flatMap((savedProduct) => savedProduct.id)
    )
    const [modalOpen, setModalOpen] = useState<boolean>(false)

    const { articles } = articleListingWithImage

    const getArticleListingWithImageIndex = useCallback((productsLength: number) => {
      const insertIndex = 3
      return productsLength && productsLength > insertIndex ? insertIndex : productsLength - 1
    }, [])

    const articleListingWithImageIndex = articles.length
      ? getArticleListingWithImageIndex(products?.length || 0)
      : null

      const {
        compareTrayItems,
        addProduct,
        removeAllProducts,
        removeHandler,
        removeProductFromCardHandler,
        setTaxonomySlug,
        taxonomySlug: currentTaxonomySlug
      } = useContext(CompareTrayContext);
      
      useEffect(() => {
        if (taxonomySlug && taxonomySlug !== currentTaxonomySlug) {
          setTaxonomySlug(taxonomySlug);
        }
      }, [setTaxonomySlug, taxonomySlug, currentTaxonomySlug]);

    const compareButtonClickHandler = () => {
      dynamicDatalayerPush({
        eventAction: 'Compare - Compare Tray',
        eventCategory: 'Product Compare',
        eventLabel: '',
      })
    }

    if ((products?.length || 0) === 0) {
      return (
        <section {...rest}>
          <InfoBanner content={NO_AVAILABLE_PRODUCTS} context="search-alert" />
          <ArticleListingWithImage {...articleListingWithImage} />
        </section>
      )
    }

    const updateSavedProducts = (productId: string) => {
      if (loginStatus.includes('NO_ACCESS')) {
        setModalOpen(true)
        return
      }

      setSavedProductsState((prevSavedProducts) => {
        const newState = prevSavedProducts.includes(productId)
          ? prevSavedProducts.filter((product) => product !== productId)
          : [...prevSavedProducts, productId]

        return newState
      })
    }

    return (
      <>
        <section {...rest}>
          <InfoBanner
            title={scoreStatement?.title && `What is '${scoreStatement?.title}'?`}
            content={scoreStatement?.content}
            context="search-alert"
          />
          <ul className={styles.productsList} ref={ref}>
            {products?.map((product, index) => {
              const isSelectedForComparison = getIsSelectedForComparison({
                compareTrayItems,
                slug: product.slug || '',
              })

              return (
                <Fragment key={product.businessKey}>
                  <li className={styles.productItem}>
                    <ProductCard
                      product={product}
                      taxonomySlug={taxonomySlug}
                      index={index}
                      compareTrayItems={compareTrayItems}
                      addCompareProduct={addProduct}
                      removeCompareProduct={removeProductFromCardHandler}
                      savedProducts={savedProductsState}
                      savedCallback={updateSavedProducts}
                      loginStatus={loginStatus}
                    />
                    {isSelectedForComparison && compareTrayItems?.length && (
                      <SkipButton
                        className={styles.productItemSkipButton}
                        selector='button[data-testid="reviews-compare-button"]'
                      >
                        Skip to Compare Tray
                      </SkipButton>
                    )}
                  </li>
                  {index === articleListingWithImageIndex && (
                    <li key="article-listing-with-image" className={styles.needHelp}>
                      <ArticleListingWithImage {...articleListingWithImage} />
                    </li>
                  )}
                </Fragment>
              )
            })}
          </ul>
          {(compareTrayItems?.length ?? 0) > 0 && (
            <ReviewsCompareTray
              taxonomySlug={taxonomySlug}
              products={compareTrayItems}
              removeHandler={removeHandler}
              removeAllHandler={removeAllProducts}
              compareHandler={compareButtonClickHandler}
              skipToContent={{
                selector: 'section[data-testid="product-list-header"]',
                text: 'Return to Products',
              }}
            />
          )}
        </section>
        {modalOpen &&
          createPortal(
            <SignUp
              className={styles.modal}
              image={modalImageProps}
              overline="Recommended"
              title="Full access"
              closeModal={() => setModalOpen(false)}
              modalLinkDatalayerTrackingId={'savebutton'}
            />,
            document.body
          )}
      </>
    )
  }
)

////// IMPLEMENTATION ///////

type ReviewsProductsProps = {
  products?: ProductCard[]
  taxonomySlug: ProductListingPageQuery['productListingPage']['productsWithPagination']['taxonomySlug']
  scoreStatement?: ProductListingPageQuery['productListingPage']['scoreStatement']
  articleListingWithImage: ProductListingPageQuery['productListingPage']['articleListingWithImage']
  loginStatus: string
  savedProducts: SavedItemsQuery['savedItems'][number]['products']
}

export interface Variant {
  image: ImageWithSources
  model: string
  slug: string
  variantValues: VariantValue[]
  businessKey: string
  badges?: string[]
  tags?: string[]
  hasRetailers?: boolean
}

type VariantValue = {
  code: string
  value: string
  label: string
}

export type ProductCard = {
  slug?: string
  model?: string
  badges?: string[]
  manufacturer?: string
  price?: string
  image?: ImageWithSources
  features: Features[]
  tags?: string[]
  retailers?: boolean
  alertType: string
  category?: string
  productScoreGauge?: {
    label?: string
    testScoreGauge?: TestScore
    tooltip?: TooltipWithButtonProps
  }
  businessKey?: string
  annualRunningCost?: AnnualRunningCostType
  variantProducts: Variant[]
  variantPriceRange?: {
    min: VariantPrice
    max: VariantPrice
  }
}

type VariantPrice = {
  formatted: string
}
